import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useNotify, useTranslate } from 'react-admin';
import { downloadBlob } from 'src/utils/format';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';

import CopyCell from '../../components/CopyCell';
import { DashboardV3RangeSelector } from '../../components/DashboardV3RangeSelector';
import { useDashboardV3Range } from '../../hooks/useDashboardV3Range';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useDashboardV3UserData } from '../../hooks/useDashboardV3UserData';
import { exportDashboardV3UserData } from '../../services/dashboardV3.service';
import {
    DASHBOARD_V3_USER_LOGIN_METHOD_CHIPS,
    DASHBOARD_V3_USER_RANGE_LABELS,
    DASHBOARD_V3_USER_STATUS_CHIPS,
    DASHBOARD_V3_USER_TABLE_COLUMNS,
} from '../../mock-data';
import type {
    DashboardV3UserActiveStatusFilter,
    DashboardV3UserDataParams,
    DashboardV3UserLoginMethodFilter,
    DashboardV3UserRangeValue,
    DashboardV3UserRow,
    DashboardV3UserSortOrder,
    DashboardV3UserTableColumn,
    DashboardV3UserTableColumnKey,
} from '../../services/dashboardV3.types';

const ACCENT_COLORS: Record<string, string> = {
    primary: '#BB86FC',
    secondary: '#03DAC6',
    success: '#81C995',
    warning: '#FFB74D',
    info: '#64B5F6',
    error: '#CF6679',
};

const COPY_COLUMN_KEYS: DashboardV3UserTableColumnKey[] = [
    'userId',
    'walletAddress',
    'loginAccount',
    'parentWalletAddress',
];

const searchInputSx = {
    flexShrink: 0,

    '& .MuiOutlinedInput-root': {
        height: 36,
        minHeight: 36,
        borderRadius: '4px',
        bgcolor: 'transparent',
        color: '#FFFFFF',
        fontSize: 14,
        lineHeight: '36px',
        boxSizing: 'border-box',
    },

    '& .MuiOutlinedInput-input': {
        height: 36,
        minHeight: 36,
        lineHeight: '36px',
        padding: '0 14px',
        boxSizing: 'border-box',
        fontSize: 14,

        '&::placeholder': {
            color: 'rgba(255,255,255,0.45)',
            opacity: 1,
        },
    },

    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.16)',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(187,134,252,0.55)',
    },

    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BB86FC !important',
        borderWidth: '1px !important',
    },
} as const;

function formatInteger(value?: number | null) {
    return Number(value || 0).toLocaleString('en-US');
}

function formatMoney(value?: number | string | null, options?: { showPlus?: boolean; zeroDash?: boolean }) {
    const n = Number(value || 0);

    if (options?.zeroDash && n === 0) return '—';

    const sign = n < 0 ? '-' : options?.showPlus && n > 0 ? '+' : '';
    const abs = Math.abs(n);

    return `${sign}$${abs.toLocaleString('en-US', {
        maximumFractionDigits: 2,
    })}`;
}

function formatDelta(value?: number | null) {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '--';
    }

    return `${value > 0 ? '+' : ''}${value}%`;
}

function getDeltaTrend(value?: number | null): 'up' | 'down' {
    return Number(value || 0) < 0 ? 'down' : 'up';
}

function isMoneyColumn(key: DashboardV3UserTableColumnKey) {
    return key.endsWith('Volume') || key.endsWith('Pnl') || key.endsWith('DepositAmount');
}

function isCountColumn(key: DashboardV3UserTableColumnKey) {
    return key.endsWith('DepositCount');
}

function getMetricColor(key: DashboardV3UserTableColumnKey, value: unknown) {
    const n = Number(value || 0);

    if (key.endsWith('Pnl')) {
        if (n > 0) return '#81C995';
        if (n < 0) return '#CF6679';
        return 'rgba(255,255,255,0.45)';
    }

    if (key.includes('predictionSpot')) return '#03DAC6';
    if (key.includes('predictionLeverage')) return '#BB86FC';
    if (key.includes('predictionGrid')) return '#81C995';
    if (key.includes('perpetual')) return '#FFB74D';

    return 'rgba(255,255,255,0.86)';
}

function formatCellValue(row: DashboardV3UserRow, column: DashboardV3UserTableColumn) {
    const value = row[column.key];

    if (isMoneyColumn(column.key)) {
        return formatMoney(value as number, {
            showPlus: column.key.endsWith('Pnl'),
            zeroDash: column.key.endsWith('Pnl'),
        });
    }

    if (isCountColumn(column.key)) {
        return formatInteger(Number(value || 0));
    }

    return String(value || '-');
}

interface MetricCardProps {
    title: string;
    value: string;
    delta: string;
    deltaNote: string;
    subtitle: string;
    variant?: keyof typeof ACCENT_COLORS;
    trend?: 'up' | 'down';
    loading?: boolean;
}

function MetricCard({
    title,
    value,
    delta,
    deltaNote,
    subtitle,
    variant = 'primary',
    trend = 'up',
    loading = false,
}: MetricCardProps) {
    const deltaColor = trend === 'down' ? '#CF6679' : '#81C995';
    const deltaPrefix = delta === '--' ? '' : trend === 'down' ? '↓ ' : '↑ ';

    return (
        <Paper
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#232323',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 0.6,
                minHeight: 132,
                p: 2.2,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: '0 0 auto 0',
                    height: 3,
                    bgcolor: ACCENT_COLORS[variant],
                }}
            />

            <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13, mb: 1.2 }}>
                {title}
            </Typography>

            <Typography
                sx={{
                    color: '#FFFFFF',
                    fontSize: 31,
                    lineHeight: 1.1,
                    fontWeight: 500,
                    letterSpacing: 0.5,
                }}
            >
                {loading ? '--' : value}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1.2 }}>
                <Typography sx={{ color: deltaColor, fontSize: 13 }}>
                    {loading ? '--' : `${deltaPrefix}${delta.replace('-', '')}`}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                    {deltaNote}
                </Typography>
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 12, mt: 0.8 }}>
                {subtitle}
            </Typography>
        </Paper>
    );
}

interface FilterChipGroupProps<T extends string> {
    label: string;
    value: T;
    options: readonly {
        labelKey: string;
        label?: string;
        value: T;
    }[];
    onChange: (value: T) => void;
}

function FilterChipGroup<T extends string>({
    label,
    value,
    options,
    onChange,
}: FilterChipGroupProps<T>) {
    const t = useTranslate();

    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, mr: 0.5 }}>
                {label}
            </Typography>

            {options.map((item) => {
                const active = value === item.value;

                return (
                    <Chip
                        key={item.value}
                        label={t(item.labelKey, { _: item.label ?? item.labelKey })}
                        size="medium"
                        clickable
                        onClick={() => onChange(item.value)}
                        variant="outlined"
                        sx={{
                            height: 30,
                            borderRadius: 2,
                            borderColor: active ? '#BB86FC' : 'rgba(255,255,255,.12)',
                            bgcolor: active ? 'rgba(187, 134, 252, .16)' : 'transparent',
                            color: active ? '#BB86FC' : 'rgba(255,255,255,.65)',
                            '& .MuiChip-label': {
                                px: 1.4,
                                fontSize: 13,
                            },
                        }}
                    />
                );
            })}
        </Stack>
    );
}

function StatusCell({ status }: { status: DashboardV3UserRow['status'] }) {
    const t = useTranslate();
    // 后端返回的状态可能是 'ACTIVE' / 'Active' 等不同大小写，做归一化比较
    const active = String(status ?? '').trim().toLowerCase() === 'active';

    return (
        <Chip
            label={
                active
                    ? t('module.dashboardV3.users.status.active', { _: 'Active' })
                    : t('module.dashboardV3.users.status.inactive', { _: 'Inactive' })
            }
            size="small"
            sx={{
                height: 24,
                minWidth: 64,
                borderRadius: 1,
                border: `1px solid ${active ? 'rgba(129,201,149,0.35)' : 'rgba(255,255,255,0.08)'}`,
                '&&': {
                    bgcolor: active ? 'rgba(129,201,149,0.14)' : 'rgba(255,255,255,0.06)',
                    color: active ? '#81C995' : 'rgba(255,255,255,0.62)',
                },
                '& .MuiChip-label': {
                    px: 1,
                    fontSize: 12,
                    color: active ? '#81C995' : 'rgba(255,255,255,0.62)',
                },
            }}
        />
    );
}

function UserTableCell({
    row,
    column,
    copiedText,
}: {
    row: DashboardV3UserRow;
    column: DashboardV3UserTableColumn;
    copiedText: string;
}) {
    const value = row[column.key];

    if (column.key === 'status') {
        return <StatusCell status={row.status} />;
    }

    if (
        (column.key === 'loginAccount' || column.key === 'loginMethod') &&
        (value === null || value === undefined || value === '')
    ) {
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13 }}>
                --
            </Typography>
        );
    }

    if (COPY_COLUMN_KEYS.includes(column.key)) {
        return (
            <CopyCell
                text={value as string}
                shorten
                copiedText={copiedText}
                minWidth={column.key === 'userId' ? 94 : 112}
                style={{
                    color: column.key === 'walletAddress' ? '#FFFFFF' : 'rgba(255,255,255,0.82)',
                }}
            />
        );
    }

    return (
        <Typography
            component="span"
            sx={{
                color: column.metric ? getMetricColor(column.key, value) : 'rgba(255,255,255,0.82)',
                fontSize: 13,
                fontFamily: column.mono ? 'Roboto Mono, monospace' : 'inherit',
                whiteSpace: 'nowrap',
            }}
        >
            {formatCellValue(row, column)}
        </Typography>
    );
}

function TableEmpty({ colSpan, text }: { colSpan: number; text: string }) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                sx={{
                    py: 6,
                    borderBottom: 'none',
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.45)',
                }}
            >
                {text}
            </TableCell>
        </TableRow>
    );
}

export function UsersPage() {
    const t = useTranslate();
    const notify = useNotify();

    const {
        range,
        setRange,
        customFrom,
        setCustomFrom,
        customTo,
        setCustomTo,
        rangeParams,
    } = useDashboardV3Range<DashboardV3UserRangeValue>('today');

    const [loginMethod, setLoginMethod] = useState<DashboardV3UserLoginMethodFilter>('all');
    const [activeStatus, setActiveStatus] = useState<DashboardV3UserActiveStatusFilter>('all');

    const [userIdInput, setUserIdInput] = useState('');
    const [walletAddressInput, setWalletAddressInput] = useState('');
    const [loginAccountInput, setLoginAccountInput] = useState('');
    const [parentWalletAddressInput, setParentWalletAddressInput] = useState('');

    const userId = useDebouncedValue(userIdInput.trim(), 500);
    const walletAddress = useDebouncedValue(walletAddressInput.trim(), 500);
    const loginAccount = useDebouncedValue(loginAccountInput.trim(), 500);
    const parentWalletAddress = useDebouncedValue(parentWalletAddressInput.trim(), 500);

    const [page, setPage] = useState(0);
    // 列表固定每页 25 条，不提供其他每页条数选项
    const rowsPerPage = 25;

    const [sortKey, setSortKey] = useState<DashboardV3UserTableColumnKey>('registeredAt');
    const [sortDirection, setSortDirection] = useState<DashboardV3UserSortOrder>('desc');

    const [exporting, setExporting] = useState(false);

    const hasSearchValue = Boolean(
        userIdInput || walletAddressInput || loginAccountInput || parentWalletAddressInput
    );

    // 列表数据与时间范围无关，因此切换 range/自定义时间不重置分页；
    // 只有筛选条件变化时才回到第一页。
    useEffect(() => {
        setPage(0);
    }, [
        loginMethod,
        activeStatus,
        userId,
        walletAddress,
        loginAccount,
        parentWalletAddress,
    ]);

    const queryParams = useMemo<DashboardV3UserDataParams>(
        () => ({
            ...rangeParams,
            page,
            size: rowsPerPage,
            loginMethod,
            activeStatus,
            sortBy: sortKey,
            sortOrder: sortDirection,
            userId,
            walletAddress,
            loginAccount,
            parentWalletAddress,
        }),
        [
            rangeParams,
            page,
            rowsPerPage,
            loginMethod,
            activeStatus,
            sortKey,
            sortDirection,
            userId,
            walletAddress,
            loginAccount,
            parentWalletAddress,
        ]
    );

    const {
        kpi,
        list,
        total,
        kpiLoading,
        usersLoading,
    } = useDashboardV3UserData(queryParams);

    const metricCards = useMemo(
        () => [
            {
                key: 'totalUsers',
                title: t('module.dashboardV3.users.kpi.totalUsers', { _: 'Total Registered Users' }),
                value: formatInteger(kpi.totalUsers),
                // 这张卡片特殊：下方不做环比，恒显示「今日新增注册用户数」
                delta: formatInteger(kpi.totalUsersToday),
                deltaNote: t('module.dashboardV3.common.today', { _: 'Today' }),
                subtitle: t('module.dashboardV3.users.kpi.totalUsersSubtitle', {
                    _: 'Cumulative registered users',
                }),
                variant: 'primary' as const,
                trend: 'up' as const,
            },
            {
                key: 'newUsers',
                title: t('module.dashboardV3.users.kpi.newUsers', { _: 'New Registered Users' }),
                value: formatInteger(kpi.newUsers),
                delta: formatDelta(kpi.newUsersDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t('module.dashboardV3.users.kpi.newUsersSubtitle', {
                    _: 'New accounts created in selected period',
                }),
                variant: 'secondary' as const,
                trend: getDeltaTrend(kpi.newUsersDelta),
            },
            {
                key: 'activeUsers',
                title: t('module.dashboardV3.users.kpi.activeUsers', { _: 'Active Users' }),
                value: formatInteger(kpi.activeUsers),
                delta: formatDelta(kpi.activeUsersDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t('module.dashboardV3.users.kpi.activeUsersSubtitle', {
                    _: 'Users active in selected period',
                }),
                variant: 'info' as const,
                trend: getDeltaTrend(kpi.activeUsersDelta),
            },
            {
                key: 'depositUsers',
                title: t('module.dashboardV3.users.kpi.depositUsers', { _: 'Deposit Users' }),
                value: formatInteger(kpi.depositUsers),
                delta: formatDelta(kpi.depositUsersDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t('module.dashboardV3.users.kpi.depositUsersSubtitle', {
                    _: 'Users with deposit behavior in selected period',
                }),
                variant: 'success' as const,
                trend: getDeltaTrend(kpi.depositUsersDelta),
            },
        ],
        [kpi, t]
    );

    const handleSort = useCallback(
        (key: DashboardV3UserTableColumnKey) => {
            if (sortKey === key) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortKey(key);
                setSortDirection('desc');
            }

            setPage(0);
        },
        [sortKey]
    );

    const handleClearSearch = useCallback(() => {
        setUserIdInput('');
        setWalletAddressInput('');
        setLoginAccountInput('');
        setParentWalletAddressInput('');
    }, []);

    const handleExportCsv = useCallback(async () => {
        if (exporting) return;

        setExporting(true);

        try {
            const { blob, filename } = await exportDashboardV3UserData({
                ...queryParams,
                page: 0,
                size: total || rowsPerPage,
                format: 'csv',
            });

            downloadBlob(filename, blob);
            notify(t('module.dashboardV3.users.messages.exportSuccess', { _: 'CSV exported' }), {
                type: 'info',
            });
        } catch (e: any) {
            notify(
                e?.message ||
                t('module.dashboardV3.users.messages.exportFailed', { _: 'Export failed' }),
                { type: 'warning' }
            );
        } finally {
            setExporting(false);
        }
    }, [exporting, notify, queryParams, rowsPerPage, t, total]);

    const copiedText = t('module.dashboardV3.common.copied', { _: 'Copied' });

    return (
        <PermissionGuard resource="dashboard-v3" action="read" fallback={<ErrorPage403 />}>
            <Box sx={{ mt: 3, color: '#FFFFFF' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.6 }}>
                            {t('module.dashboardV3.users.title', { _: 'User Data' })}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                            {t('module.dashboardV3.users.subtitle', {
                                _: 'User Management · Full user profile',
                            })}
                        </Typography>
                    </Box>

                    <DashboardV3RangeSelector
                        value={range}
                        options={DASHBOARD_V3_USER_RANGE_LABELS}
                        customFrom={customFrom}
                        customTo={customTo}
                        onChange={setRange}
                        onCustomFromChange={setCustomFrom}
                        onCustomToChange={setCustomTo}
                    />
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: 2,
                        mb: 2.5,
                    }}
                >
                    {metricCards.map((item) => (
                        <MetricCard
                            key={item.key}
                            title={item.title}
                            value={item.value}
                            delta={item.delta}
                            deltaNote={item.deltaNote}
                            subtitle={item.subtitle}
                            variant={item.variant}
                            trend={item.trend}
                            loading={kpiLoading}
                        />
                    ))}
                </Box>

                <Paper
                    sx={{
                        bgcolor: '#232323',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 0.8,
                        overflow: 'hidden',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        alignItems={{ xs: 'flex-start', lg: 'center' }}
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                            px: 2,
                            py: 2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                        }}
                    >
                        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                            <FilterChipGroup
                                label={t('module.dashboardV3.filters.loginMethod', { _: 'Login Method' })}
                                value={loginMethod}
                                options={DASHBOARD_V3_USER_LOGIN_METHOD_CHIPS}
                                onChange={(value) => {
                                    setLoginMethod(value);
                                    setPage(0);
                                }}
                            />

                            {/* <FilterChipGroup
                                label={t('module.dashboardV3.users.filters.activeStatus', { _: 'Active Status' })}
                                value={activeStatus}
                                options={DASHBOARD_V3_USER_STATUS_CHIPS}
                                onChange={(value) => {
                                    setActiveStatus(value);
                                    setPage(0);
                                }}
                            /> */}
                        </Stack>

                        <PermissionButton resource="dashboard-v3" action="export">
                            <Button
                                startIcon={
                                    exporting ? (
                                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                    ) : (
                                        <FileDownloadOutlinedIcon />
                                    )
                                }
                                onClick={handleExportCsv}
                                disabled={usersLoading || exporting}
                                sx={{
                                    color: '#BB86FC',
                                    fontWeight: 700,
                                    letterSpacing: 0.8,
                                    '&:hover': {
                                        bgcolor: 'rgba(187,134,252,0.08)',
                                    },
                                    '&.Mui-disabled': {
                                        color: 'rgba(187,134,252,0.35)',
                                    },
                                }}
                            >
                                {exporting
                                    ? t('module.dashboardV3.actions.exporting', { _: 'Exporting...' })
                                    : t('module.dashboardV3.actions.exportCsv', { _: 'Export CSV' })}
                            </Button>
                        </PermissionButton>
                    </Stack>

                    <Box
                        sx={{
                            px: 2,
                            py: 2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            bgcolor: '#232323',
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder={t('module.dashboardV3.filters.userIdPlaceholder', { _: 'User ID' })}
                            value={userIdInput}
                            onChange={(e) => setUserIdInput(e.target.value)}
                            sx={{ ...searchInputSx, width: 172 }}
                        />

                        <TextField
                            size="small"
                            placeholder={t('module.dashboardV3.users.filters.userAddressPlaceholder', {
                                _: 'User Address',
                            })}
                            value={walletAddressInput}
                            onChange={(e) => setWalletAddressInput(e.target.value)}
                            sx={{ ...searchInputSx, width: 260 }}
                        />

                        <TextField
                            size="small"
                            placeholder={t('module.dashboardV3.filters.loginAccountPlaceholder', {
                                _: 'Login Account',
                            })}
                            value={loginAccountInput}
                            onChange={(e) => setLoginAccountInput(e.target.value)}
                            sx={{ ...searchInputSx, width: 260 }}
                        />

                        <TextField
                            size="small"
                            placeholder={t('module.dashboardV3.users.filters.parentAddressPlaceholder', {
                                _: 'Parent Address',
                            })}
                            value={parentWalletAddressInput}
                            onChange={(e) => setParentWalletAddressInput(e.target.value)}
                            sx={{ ...searchInputSx, width: 260 }}
                        />

                        {hasSearchValue && (
                            <Button
                                variant="text"
                                startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                                onClick={handleClearSearch}
                                sx={{
                                    height: 36,
                                    px: 1.5,
                                    color: '#BB86FC',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: 'rgba(187,134,252,0.08)',
                                    },
                                }}
                            >
                                {t('module.dashboardV3.actions.clear', { _: 'Clear' })}
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ position: 'relative' }}>
                        {usersLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(18,18,18,0.45)',
                                    backdropFilter: 'blur(1px)',
                                    transition: 'opacity 0.15s ease',
                                }}
                            >
                                <CircularProgress size={28} sx={{ color: '#BB86FC' }} />
                            </Box>
                        )}

                    <TableContainer
                        sx={{
                            maxWidth: '100%',
                            overflowX: 'auto',
                            bgcolor: '#232323',
                            '&::-webkit-scrollbar': {
                                height: 8,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: 'rgba(255,255,255,0.16)',
                                borderRadius: 8,
                            },
                        }}
                    >
                        <Table stickyHeader size="small" sx={{ minWidth: 4700 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04) !important' }}>
                                    {DASHBOARD_V3_USER_TABLE_COLUMNS.map((column) => {
                                        const active = sortKey === column.key;

                                        return (
                                            <TableCell
                                                key={column.key}
                                                align={column.align ?? 'left'}
                                                sortDirection={active ? sortDirection : false}
                                                sx={{
                                                    minWidth: column.minWidth,
                                                    height: 52,
                                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                    color: 'rgba(255,255,255,0.74)',
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 2,
                                                    // 主题对 stickyHeader 设了 background.paper 底色 + 一层 background.neutral 的渐变
                                                    // backgroundImage（亮色模式下为浅色），仅覆盖 backgroundColor 不够，必须同时清掉渐变
                                                    bgcolor: '#26323E !important',
                                                    backgroundImage: 'none !important',
                                                }}
                                            >
                                                {column.sortable ? (
                                                    <TableSortLabel
                                                        active={active}
                                                        direction={active ? sortDirection : 'asc'}
                                                        onClick={() => handleSort(column.key)}
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.74) !important',
                                                            bgcolor: '#26323E !important',
                                                            '& .MuiTableSortLabel-icon': {
                                                                color: '#BB86FC !important',
                                                                bgcolor: '#26323E !important',
                                                            },
                                                            '&:hover': {
                                                                color: '#FFFFFF !important',
                                                            },
                                                        }}
                                                    >
                                                        {t(column.labelKey, { _: column.label })}
                                                    </TableSortLabel>
                                                ) : (
                                                    t(column.labelKey, { _: column.label })
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {list.length === 0 ? (
                                    // 仅在没有任何数据时占位：加载中显示 Loading，否则显示无数据；
                                    // 有数据时保持旧数据展示，加载状态由上层遮罩呈现，避免排序/翻页时表格突然清空
                                    <TableEmpty
                                        colSpan={DASHBOARD_V3_USER_TABLE_COLUMNS.length}
                                        text={
                                            usersLoading
                                                ? t('module.dashboardV3.common.loading', { _: 'Loading...' })
                                                : t('module.dashboardV3.common.noData', { _: 'No data' })
                                        }
                                    />
                                ) : (
                                    list.map((row) => (
                                        <TableRow
                                            key={row.id || row.userId}
                                            hover
                                            sx={{
                                                bgcolor: '#202020',
                                                '&:nth-of-type(even)': {
                                                    bgcolor: '#1D1D1D',
                                                },
                                                '&:hover': {
                                                    bgcolor: 'rgba(187,134,252,0.08) !important',
                                                },
                                            }}
                                        >
                                            {DASHBOARD_V3_USER_TABLE_COLUMNS.map((column) => (
                                                <TableCell
                                                    key={`${row.id || row.userId}-${column.key}`}
                                                    align={column.align ?? 'left'}
                                                    sx={{
                                                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                                                        py: 1.45,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <UserTableCell row={row} column={column} copiedText={copiedText} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[]}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                color: 'rgba(255,255,255,0.58)',
                                fontSize: 13,
                            },
                            '& .MuiSelect-select': {
                                color: '#FFFFFF',
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'rgba(255,255,255,0.72)',
                            },
                        }}
                    />
                </Paper>
            </Box>
        </PermissionGuard>
    );
}
